import type { SalesChannel } from '@/types/integration';
import { format } from 'date-fns';
import { Box, Card, CardHeader, Chip, Table, TableBody, TableHead, TableCell, TableRow, Typography, Link } from '@mui/material';
import { formatPrice } from '../../components/misc/format-price';
import { statusColor } from '../../components/misc/status-color';
import { useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/ssr';
import NextLink from 'next/link';
import { findSalesChannelById } from '../../contexts/IntegrationsContext';
import { blankImg } from '../../components/misc/blank-img';
import { useField } from '../../hooks/useField';
import { getLatestOrders } from '@/endpoints/dashboard/getLatestOrders';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { requestScopeFor } from '@/session/scopedTransport';
import { utils } from '../../utils';
import toast from 'react-hot-toast';
import { acquireOperationContext, operationRequirement } from '@/session/operation';

type Order = {
  id: string | number;
  created_at: string;
  sales_channel_id: string | number;
  sales_channel_name: string;
  external_order_id: string;
  status: string;
  total: number;
};

export interface OverviewLatestOrdersProps {
  timezone: string;
  sChannels: SalesChannel[];
  sChannelsLoading: boolean;
  [key: string]: any; // for other card props
}

export const OverviewLatestOrders: React.FC<OverviewLatestOrdersProps> = ({ timezone, sChannels, sChannelsLoading, ...props }) => {
  const orders = useField<Order[]>([]);
  const { keycloak, initialized } = useKeycloak();
  const organization = useCommittedOrganization();
  const requestSequenceRef = useRef(0);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!timezone) return;
      const operation = acquireOperationContext(organization);
      if (!operation.isScopeCurrent()) return;
      const sequence = (requestSequenceRef.current += 1);

      const response = await getLatestOrders(
        requestScopeFor(organization),
        {
          limit: 10,
          timezone,
        },
        {
          signal: operation.scopeSignal,
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      );
      if (sequence !== requestSequenceRef.current || !operation.isScopeCurrent()) return;

      if (response.isError) {
        orders.setValue([]);
        if (!utils.error.isCanceledResponse(response)) toast.error(response.errors.error ?? 'Failed to get latest orders');
        return;
      }

      orders.setValue(response.result);
    };

    if (keycloak?.authenticated && initialized) {
      void fetchOrders();
    }

    return () => {
      requestSequenceRef.current += 1;
    };
  }, [initialized, keycloak?.authenticated, organization, timezone]);

  return (
    <Card {...props}>
      <CardHeader sx={{ p: 2 }} title='Latest Orders' />
      <Box sx={{ overflow: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={2}>Order Details</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(orders.value) &&
              orders.value.map((order) => (
                <TableRow
                  key={order.id}
                  sx={{
                    '&:last-child td': {
                      border: 0,
                    },
                  }}
                >
                  <TableCell sx={{ width: '1%' }}>
                    <Box
                      sx={{
                        py: 0.25,
                        px: 1,
                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.200'),
                        borderRadius: 2,
                        maxWidth: 'fit-content',
                      }}
                    >
                      <Typography align='center' color='textSecondary' variant='subtitle2'>
                        {format(new Date(order.created_at), 'MMM').toUpperCase()}
                      </Typography>
                      <Typography align='center' color='textSecondary' variant='h6'>
                        {format(new Date(order.created_at), 'd')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <div>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {(() => {
                          const sc = findSalesChannelById(sChannels, order.sales_channel_id);
                          const iconUrl = sc?.borrowed_icon_url || blankImg;
                          return (
                            <Box sx={{ display: 'flex', width: '2.5rem', p: 0.5, backgroundColor: 'white', borderRadius: '.5rem' }}>
                              <img src={iconUrl} width={32} height={32} alt='' />
                            </Box>
                          );
                        })()}
                        <Box>
                          <Box>{order.sales_channel_name}</Box>
                          <Link
                            component={NextLink}
                            href={`/${organization}/orders/${order.id}`}
                            color='textPrimary'
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                            }}
                          >
                            {order.external_order_id}
                          </Link>
                        </Box>
                      </Box>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip color={statusColor(order.status)} label={order.status} />
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='subtitle2'>{formatPrice(order.total, false)}</Typography>
                    <Typography color='textSecondary' variant='body2'></Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
